import { isProd, isStg } from "@/env";

interface FixStudentRequest {
  schoolDocId?: string;
  schoolDocIds?: string[];
}

interface IrregularDuplicate {
  studentDocId: string;
  reason: string;
}

/** fix_student_duplications のレスポンス */
export interface FixStudentDuplicationsResponseItem {
  schoolDocId: string;
  status: "success" | "partial_failure" | "failure" | "no_duplicates";
  succeededStudentIds?: string[];
  failedStudentIds?: string[];
  irregularDuplicates?: IrregularDuplicate[];
  errorMessage?: string;
}

/** コレクションコピー時のエラー詳細 */
interface CollectionError {
  studentDocId: string;
  classroomDocId: string;
  collectionName: string;
  errorMessage: string;
}

/** fix_student_sub_collection_transfer のレスポンス */
export interface FixSubCollectionTransferResponseItem {
  schoolDocId: string;
  /**
   * no_target: ゾンビ生徒なし（正常）
   * success: 全て成功
   * partial_success: 一部成功、一部失敗
   * all_skipped: ゾンビはいたが全てスキップ（対応する正規の生徒が存在しない）
   * failure: 処理全体が失敗
   */
  status:
    | "no_target"
    | "success"
    | "partial_success"
    | "all_skipped"
    | "failure";
  errorMessage?: string;
  errors?: CollectionError[];
  /** 処理を試みたゾンビ数 */
  processedZombieCount?: number;
  /** 成功したゾンビ数 */
  succeededZombieCount?: number;
  /** スキップしたゾンビ数 */
  skippedZombieCount?: number;
  /** 実際にコピーしたドキュメント数 */
  copiedDocCount?: number;
}

/** 会話のclassroomId修正時の失敗情報 */
interface ConversationFixFailure {
  studentDocId: string;
  classroomId: string;
  failedConversationIds: string[];
}

/** 成功・部分成功時のレスポンス */
interface FixClassroomIdSuccessResponse {
  schoolDocId: string;
  status: "success" | "partial";
  /** 確認した生徒数（全生徒） */
  checkedStudentCount: number;
  /** 修正が必要だった生徒数 */
  fixedStudentCount: number;
  /** 修正した会話数 */
  fixedConversationCount: number;
  failures: ConversationFixFailure[];
}

/** 失敗時のレスポンス */
interface FixClassroomIdFailureResponse {
  schoolDocId: string;
  status: "failure";
  errorMessage: string;
}

export type FixClassroomIdResponseItem =
  | FixClassroomIdSuccessResponse
  | FixClassroomIdFailureResponse;

/** ジャーナルのclassroomId修正時の失敗情報 */
interface JournalReflectionFixFailure {
  studentDocId: string;
  classroomId: string;
  failedReflectionIds: string[];
}

/** 成功・部分成功時のレスポンス */
interface FixJournalReflectionSuccessResponse {
  schoolDocId: string;
  status: "success" | "partial";
  /** 確認した生徒数（全生徒） */
  checkedStudentCount: number;
  /** 修正が必要だった生徒数 */
  fixedStudentCount: number;
  /** 修正したリフレクション数 */
  fixedReflectionCount: number;
  failures: JournalReflectionFixFailure[];
}

/** 失敗時のレスポンス */
interface FixJournalReflectionFailureResponse {
  schoolDocId: string;
  status: "failure";
  errorMessage: string;
}

export type FixJournalReflectionResponseItem =
  | FixJournalReflectionSuccessResponse
  | FixJournalReflectionFailureResponse;

function getCloudFunctionsBaseUrl(): string {
  if (isProd) {
    return "https://asia-northeast1-mingaku-jp.cloudfunctions.net";
  }
  if (isStg) {
    return "https://asia-northeast1-mingaku-st.cloudfunctions.net";
  }
  return "https://asia-northeast1-mingaku-jp-dev.cloudfunctions.net";
}

export async function fixStudentDuplications(
  params: FixStudentRequest,
  token: string
): Promise<FixStudentDuplicationsResponseItem[]> {
  const baseUrl = getCloudFunctionsBaseUrl();
  const url = `${baseUrl}/fix_student_duplications`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token
    },
    body: JSON.stringify(params)
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }

  return (await res.json()) as FixStudentDuplicationsResponseItem[];
}

export async function fixStudentSubCollectionTransfer(
  params: FixStudentRequest,
  token: string
): Promise<FixSubCollectionTransferResponseItem[]> {
  const baseUrl = getCloudFunctionsBaseUrl();
  const url = `${baseUrl}/fix_student_sub_collection_transfer`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token
    },
    body: JSON.stringify(params)
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }

  return (await res.json()) as FixSubCollectionTransferResponseItem[];
}

export async function fixStudentClassroomIdInConversations(
  params: FixStudentRequest,
  token: string
): Promise<FixClassroomIdResponseItem[]> {
  const baseUrl = getCloudFunctionsBaseUrl();
  const url = `${baseUrl}/fix_student_classroom_id_in_conversation`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token
    },
    body: JSON.stringify(params)
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }

  return (await res.json()) as FixClassroomIdResponseItem[];
}

export async function fixStudentJournalReflections(
  params: FixStudentRequest,
  token: string
): Promise<FixJournalReflectionResponseItem[]> {
  const baseUrl = getCloudFunctionsBaseUrl();
  const url = `${baseUrl}/fix_student_journal_reflections`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token
    },
    body: JSON.stringify(params)
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }

  return (await res.json()) as FixJournalReflectionResponseItem[];
}
